// src/lib/warteschlange.js
// --- WARTESCHLANGE FUER PRUEFPUNKTE UND ABSCHLUSS ------------------------------
//
// Warum das nötig ist: Bricht im Trafohäuschen die Verbindung ab, war die
// Antwort des Monteurs bisher weg -- er sah nur eine Fehlermeldung. Bei 140
// Prüfpunkten je Protokoll ist das die Stelle, an der am meisten verloren
// gehen kann.
//
// Seit v52 (11.09.2026, Gesamtpruefung) arbeitet sie nach dem Prinzip
// "erst merken, dann senden":
//   1. Jede Eingabe wird SOFORT im Geraetespeicher (localStorage) vermerkt -
//      noch bevor irgendetwas gesendet wird. Schliesst der Monteur die App
//      mitten im Senden, ist nichts verloren.
//   2. Gesendet werden nur die GEAENDERTEN Felder (update statt upsert der
//      ganzen Zeile). Vorher konnte eine verzoegert gespeicherte Bemerkung
//      die Antwort, die direkt danach getippt wurde, wieder ueberschreiben.
//   3. Je Pruefpunkt wird streng nacheinander gesendet. Vorher konnten zwei
//      Anfragen (z. B. "0.4" und "0.45") in falscher Reihenfolge ankommen.
//   4. Nach dem Senden wird der Mangel abgeglichen (n.i.O. -> Mangel da,
//      sonst weg) - auch fuer Antworten, die offline gegeben wurden. Vorher
//      bekamen Offline-Antworten nie einen Mangel.
//   5. Lehnt die Datenbank ab, wird der Eintrag NICHT stillschweigend
//      geloescht, sondern unter "abgelehnt" aufbewahrt (fuer die Fehlersuche).
//      Anmelde-Probleme (abgelaufene Sitzung) gelten wie "kein Netz".
//
// Dazu kommt der Abschluss (Unterschrift, Bewertung): Er wurde bisher nur
// nachgesendet, solange der ABSCHLUSS-Reiter genau dieses Protokolls offen
// war - in der Praxis also nie. Jetzt geht er mit derselben Automatik raus.
//
// Seit v53 (13.09.2026, nach dem ersten Testlauf) auch ZEILEN: Stammdaten im
// Protokoll (Kunde, Station, Konfiguration, Trafo, MS-Feld) und die Ohm-Werte
// der Erderanlage. Die gingen bisher direkt an die Datenbank - ohne Netz waren
// sie beim Verlassen weg. Genau das war "teilweise gespeichert, teilweise
// nicht". Und es gibt jetzt jetztSpeichern() fuer den Speichern-Knopf.

import { supabase, withTimeout, trafoBereinigen, konfigBereinigen, zeileBereinigen } from './supabase';

const SCHLUESSEL = 'etb-pruefpunkte-warten';
const SCHLUESSEL_ABGELEHNT = 'etb-pruefpunkte-abgelehnt';
const ZEILEN_SCHLUESSEL = 'etb-zeilen-warten';
const ZEILEN_ABGELEHNT = 'etb-zeilen-abgelehnt';
const ABSCHLUSS_PREFIX = 'etb-abschluss-wartend-';
const ABSCHLUSS_ABGELEHNT_PREFIX = 'etb-abschluss-abgelehnt-';
const horcher = new Set();

// --- Speicher ----------------------------------------------------------------
// Seit v57 (15.09.2026): Ist der Geraetespeicher voll (ca. 5 MB, auf dem
// iPhone weniger), schlug das Merken bisher STILL fehl - die Anzeige sagte
// "gespeichert", gemerkt war nichts, und der Abschluss meldete Erfolg ohne
// Unterschrift. Jetzt:
//   1. Zwischenspeicher (Protokoll-/Stations-/Wartungslisten, Fehlerprotokoll)
//      werden geopfert - Eingaben sind wichtiger - und es wird erneut versucht.
//   2. Klappt es trotzdem nicht, bleibt der Eintrag im Arbeitsspeicher (geht
//      raus, solange die App offen ist) und speicherIstVoll() meldet es, damit
//      der Speichern-Knopf rot warnt.
const notfall = new Map();   // Schluessel -> JSON-Text
let speicherVoll = false;

export function speicherIstVoll() { return speicherVoll; }

// Entbehrliche Zwischenspeicher loeschen. Gibt die Zahl entfernter Eintraege zurueck.
export function platzSchaffen() {
  const weg = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (k.startsWith('etb-protokoll-cache-') || k.startsWith('etb-station-cache-')
          || k.startsWith('etb-wartungen-') || k.startsWith('etb-vorwartung-') || k === 'etb-fehlerprotokoll') weg.push(k);
    }
    weg.forEach(k => localStorage.removeItem(k));
    // Abgelehnte Eintraege (auch "-mangel": ein n.i.O. ohne angelegten Mangel)
    // bleiben stehen - sie sind klein und muessen sichtbar bleiben.
  } catch { /* gesperrt */ }
  return weg.length;
}

function speichern(schluessel, obj) {
  const text = JSON.stringify(obj);
  try {
    localStorage.setItem(schluessel, text);
    notfall.delete(schluessel);
    if (notfall.size === 0) speicherVoll = false;
    return true;
  } catch { /* voll? -> Platz schaffen */ }
  try {
    platzSchaffen();
    // Die alte Kopie bleibt bewusst stehen (v63): Scheitert auch dieser
    // Versuch, laege sonst die GANZE gespeicherte Warteschlange nur noch im
    // Arbeitsspeicher - beendet das Handy die App, waeren alle Antworten weg.
    localStorage.setItem(schluessel, text);
    notfall.delete(schluessel);
    if (notfall.size === 0) speicherVoll = false;
    return true;
  } catch (e) {
    notfall.set(schluessel, text);
    if (!speicherVoll) console.error('[Warteschlange] Geraetespeicher voll - Eintrag nur im Arbeitsspeicher:', schluessel, e);
    speicherVoll = true;
    return false;
  }
}

function entfernen(schluessel) {
  notfall.delete(schluessel);
  try { localStorage.removeItem(schluessel); } catch { /* egal */ }
}

function lesenVon(schluessel) {
  try {
    const text = notfall.has(schluessel) ? notfall.get(schluessel) : localStorage.getItem(schluessel);
    return JSON.parse(text || '{}') || {};
  } catch {
    return {};
  }
}

// Alle Schluessel mit einem Praefix (Geraetespeicher + Notfall).
function schluesselMit(praefix) {
  const liste = new Set();
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(praefix)) liste.add(k);
    }
  } catch { /* gesperrt */ }
  for (const k of notfall.keys()) if (k.startsWith(praefix)) liste.add(k);
  return [...liste];
}
function lesen() { return lesenVon(SCHLUESSEL); }

function melden() {
  const n = anzahl();
  horcher.forEach(cb => { try { cb(n); } catch { /* egal */ } });
}

function schreiben(obj) {
  const ok = speichern(SCHLUESSEL, obj);
  melden();
  return ok;
}

function abschlussSchluessel() {
  return schluesselMit(ABSCHLUSS_PREFIX);
}

export function anzahl() {
  return Object.keys(lesen()).length + Object.keys(lesenVon(ZEILEN_SCHLUESSEL)).length
    + abschlussSchluessel().length;
}

// Was die Datenbank abgelehnt hat (fuer den Speichern-Knopf sichtbar machen).
export function abgelehnteAnzahl() {
  return Object.keys(lesenVon(SCHLUESSEL_ABGELEHNT)).length
    + Object.keys(lesenVon(ZEILEN_ABGELEHNT)).length
    + schluesselMit(ABSCHLUSS_ABGELEHNT_PREFIX).length;
}

export function abgelehnteLesen() {
  const liste = [];
  for (const [id, e] of Object.entries(lesenVon(SCHLUESSEL_ABGELEHNT))) {
    const pos = 'Pos. ' + (e?.pp?.pos_nummer || '?') + ' ' + (e?.pp?.bezeichnung || '');
    liste.push({ was: id.endsWith('-mangel') ? 'Mangel zu ' + pos : pos, fehler: e?.fehler });
  }
  for (const k of schluesselMit(ABSCHLUSS_ABGELEHNT_PREFIX)) {
    liste.push({ was: 'Abschluss', fehler: lesenVon(k)?.fehler });
  }
  for (const e of Object.values(lesenVon(ZEILEN_ABGELEHNT))) {
    liste.push({ was: ZEILEN_TEXT[e?.art] || e?.art, fehler: e?.fehler });
  }
  return liste;
}

// Abgelehntes zurueck in die Warteschlange (z. B. nachdem das Buero die
// Rechte angepasst hat) und erneut senden.
export async function abgelehnteErneutSenden() {
  // Regel: Was seitdem NEUER eingegeben wurde, gewinnt immer. Das Abgelehnte
  // wird nur "darunter" gelegt. Mangel-Eintraege (-mangel) werden mit dem
  // AKTUELLEN Stand des Pruefpunkts vom Server neu abgeglichen - nie mit dem
  // alten, sonst koennte eine neuere Antwort ueberschrieben werden.
  try {
    const erledigt = [];
    for (const [id, e] of Object.entries(lesenVon(SCHLUESSEL_ABGELEHNT))) {
      if (!id.endsWith('-mangel') || !navigator.onLine) continue;
      const ppId = id.slice(0, -'-mangel'.length);
      try {
        const { data: zeile, error } = await withTimeout(
          supabase.from('protokoll_modul_pruefpunkte').select('*').eq('id', ppId).maybeSingle(), 20000, 'Prüfpunkt lesen');
        if (error) throw error;
        if (zeile) await withTimeout(mangelAbgleichen(zeile, e.protokollId, e.stationId, e.userId), 20000, 'Mangel abgleichen');
        erledigt.push(id);
      } catch { /* bleibt aufbewahrt */ }
    }
    // ERST JETZT lesen: Waehrend der Anfragen oben kann etwas Neues abgelehnt
    // worden sein - ein vorher gelesener Stand wuerde es ueberschreiben.
    const pp = lesenVon(SCHLUESSEL_ABGELEHNT);
    erledigt.forEach(id => { delete pp[id]; });
    const alle = lesen();
    for (const [id, e] of Object.entries(pp)) {
      if (id.endsWith('-mangel') || !e?.pp) continue;
      const altPatch = e.patch || ohneId(e.pp);
      if (alle[id]) {
        alle[id] = { ...alle[id], patch: { ...altPatch, ...(alle[id].patch || {}) } };
      } else {
        alle[id] = { patch: altPatch, pp: e.pp, protokollId: e.protokollId, stationId: e.stationId,
                     userId: e.userId, zeit: Date.now(), version: 1 };
      }
      delete pp[id];
    }
    speichern(SCHLUESSEL, alle);
    speichern(SCHLUESSEL_ABGELEHNT, pp);

    const z = lesenVon(ZEILEN_ABGELEHNT);
    const zeilen = lesenVon(ZEILEN_SCHLUESSEL);
    for (const [k, e] of Object.entries(z)) {
      zeilen[k] = zeilen[k]
        ? { ...zeilen[k], payload: { ...(e.payload || {}), ...(zeilen[k].payload || {}) } }
        : { art: e.art, payload: e.payload, zeit: Date.now(), version: 1 };
    }
    speichern(ZEILEN_SCHLUESSEL, zeilen);
    entfernen(ZEILEN_ABGELEHNT);

    for (const k of schluesselMit(ABSCHLUSS_ABGELEHNT_PREFIX)) {
      const e = lesenVon(k);
      const id = k.slice(ABSCHLUSS_ABGELEHNT_PREFIX.length);
      if (e.patch) {
        const neuer = abschlussHolen(id);
        speichern(ABSCHLUSS_PREFIX + id, { ...e.patch, ...neuer });
      }
      entfernen(k);
    }
  } catch { /* egal */ }
  melden();
  zuletztOhneNetz = 0;
  return abarbeiten();
}

// Wie viel wartet fuer dieses Protokoll noch? (fuer den Abschluss)
// Seit 20.09.2026 zaehlen auch die STAMMDATEN-Zeilen mit (Trafo-Typenschild,
// MS-Feld, Wandler, Konfiguration, Erder-Messwerte). Vorher ging der Abschluss
// raus, solange nur noch Zeilen warteten - das Buero sah ein "fertiges"
// Protokoll ohne Typenschild und ohne Ohm-Werte.
export function anzahlFuerProtokoll(protokollId) {
  const punkte = Object.values(lesen()).filter(e => e?.protokollId === protokollId).length;
  return punkte + Object.keys(lesenVon(ZEILEN_SCHLUESSEL)).length;
}

// Noch nicht gesendete Aenderungen je Pruefpunkt: { ppId: { feld: wert } }.
// Damit zeigt ein neu geoeffnetes Protokoll den Stand des Geraets, nicht den
// aelteren Stand des Servers.
export function offeneAenderungen() {
  const aus = {};
  for (const [id, e] of Object.entries(lesen())) {
    aus[id] = e?.patch || ohneId(e?.pp);
  }
  return aus;
}

// UI kann sich anmelden, um die Anzahl anzuzeigen.
export function beiAenderung(cb) {
  horcher.add(cb);
  return () => horcher.delete(cb);
}

function ohneId(pp) {
  if (!pp) return {};
  const { id, ...rest } = pp;
  return rest;
}

// --- Fehlerarten -------------------------------------------------------------
// Unterscheidet "kein Netz" von echten Datenbankfehlern. Ein Netzproblem
// gehört in die Warteschlange, ein Constraint-Verstoß muss auffallen.
export function istNetzproblem(fehler) {
  const text = String(fehler?.message || fehler || '').toLowerCase();
  return text.includes('zu lange gedauert')      // eigener Timeout
      || text.includes('failed to fetch')
      || text.includes('networkerror')
      || text.includes('load failed')
      || text.includes('timeout')
      || text.includes('abort')                   // abgebrochene Verbindung
      || !navigator.onLine;
}

// Abgelaufene oder noch nicht erneuerte Anmeldung. Das ist KEIN Grund, eine
// Antwort wegzuwerfen - nach dem Erneuern der Sitzung geht sie durch.
function istAnmeldeproblem(fehler) {
  const text = String(fehler?.message || '').toLowerCase();
  const code = String(fehler?.code || '');
  return text.includes('jwt') || text.includes('token') || code === 'PGRST301'
      || code === '401' || fehler?.status === 401;
}

function nochmalVersuchen(fehler) {
  return istNetzproblem(fehler) || istAnmeldeproblem(fehler);
}

// Keine gueltige Anmeldung (Token-Erneuerung ohne Netz gescheitert): supabase
// schickt dann nur den oeffentlichen Schluessel, ein update trifft wegen RLS
// 0 Zeilen OHNE Fehler. Das ist kein echtes "abgelehnt" - spaeter nochmal.
async function ohneGueltigeAnmeldung() {
  try {
    const { data } = await withTimeout(supabase.auth.getSession(), 5000, 'Anmeldung prüfen');
    const s = data?.session;
    return !s?.access_token || (s.expires_at && s.expires_at * 1000 < Date.now());
  } catch {
    return true;
  }
}

function ablegenAlsAbgelehnt(id, eintrag, fehler) {
  try {
    const alle = lesenVon(SCHLUESSEL_ABGELEHNT);
    const alt = alle[id];
    // Zusammenfuehren: Eine spaeter abgelehnte Bemerkung darf die frueher
    // abgelehnte Antwort nicht ersetzen (sonst war sie nach "Erneut senden" weg).
    const patch = alt?.patch || eintrag?.patch ? { ...(alt?.patch || {}), ...(eintrag?.patch || {}) } : undefined;
    alle[id] = { ...eintrag, ...(patch ? { patch } : {}), fehler: String(fehler?.message || fehler), abgelehntAm: Date.now() };
    speichern(SCHLUESSEL_ABGELEHNT, alle);
  } catch { /* egal */ }
  console.error('[Warteschlange] Datenbank lehnt ab - aufbewahrt unter "' + SCHLUESSEL_ABGELEHNT + '":', id, fehler);
}

// --- Merken ------------------------------------------------------------------
// ppVoll: der komplette, aktuelle Stand des Pruefpunkts (fuer den Mangel)
// aenderungen: nur die Felder, die sich geaendert haben (werden gesendet)
// Vom Pruefpunkt wird nur gemerkt, was der Mangel-Abgleich und die Anzeige
// abgelehnter Eintraege brauchen - die ganze Zeile (Optionen, Beschreibung,
// Zusatzfelder) fuellte den Geraetespeicher unnoetig.
function ppKompakt(pp) {
  const k = {};
  for (const f of ['id', 'protokoll_modul_id', 'pos_nummer', 'bezeichnung', 'in_ordnung', 'wert_auswahl', 'wert_modifiers', 'bemerkung']) {
    if (pp[f] !== undefined) k[f] = pp[f];
  }
  return k;
}

export function vormerken(ppVoll, aenderungen, { protokollId, stationId, userId } = {}) {
  if (!ppVoll?.id) return;
  const alle = lesen();
  const alt = alle[ppVoll.id];
  const altPatch = alt ? (alt.patch || ohneId(alt.pp)) : {};
  alle[ppVoll.id] = {
    patch: { ...altPatch, ...ohneId(aenderungen) },
    pp: ppKompakt(ppVoll),
    protokollId: protokollId ?? alt?.protokollId ?? null,
    stationId: stationId ?? alt?.stationId ?? null,
    userId: userId ?? alt?.userId ?? null,
    zeit: Date.now(),
    version: (alt?.version || 0) + 1,
  };
  return schreiben(alle);
}

function vergessenWennUnveraendert(ppId, version) {
  const alle = lesen();
  if (alle[ppId] && (alle[ppId].version || 0) === version) {
    delete alle[ppId];
    schreiben(alle);
    return true;
  }
  return false;
}

// --- Streng nacheinander je Pruefpunkt ---------------------------------------
const ketten = new Map();
export function inReihe(schluessel, fn) {
  const vorher = ketten.get(schluessel) || Promise.resolve();
  const jetzt = vorher.catch(() => {}).then(fn);
  ketten.set(schluessel, jetzt);
  jetzt.catch(() => {}).finally(() => {
    if (ketten.get(schluessel) === jetzt) ketten.delete(schluessel);
  });
  return jetzt;
}

// --- Mangel abgleichen -------------------------------------------------------
function istNichtOk(pp) {
  const w = String(pp?.wert_auswahl || '').toLowerCase().replace(/\s+/g, '');
  return pp?.in_ordnung === false || w.includes('n.i.o');
}

function mangelBeschreibung(pp) {
  const teile = [];
  if (Array.isArray(pp.wert_modifiers) && pp.wert_modifiers.length) teile.push(pp.wert_modifiers.join(', '));
  if (pp.bemerkung && pp.bemerkung.trim()) teile.push(pp.bemerkung.trim());
  // 01.6.1 Glasbausteine (logik_invertiert): "ja" IST der Mangel - es soll nichts
  // dazu stehen (Anna-Lena 21.09.2026), der Titel sagt schon alles.
  if (teile.length === 0 && pp.logik_invertiert) return '';
  if (teile.length === 0 && pp.wert_auswahl && !/n\.?\s?i\.?\s?o/i.test(pp.wert_auswahl)) return String(pp.wert_auswahl);
  // Ohne Grund und Bemerkung: nur die Aussage - die Pos.-Nummer versteht der Kunde nicht.
  return teile.join(' - ') || 'nicht in Ordnung';
}

// n.i.O. -> genau ein offener Mangel; sonst keiner. Wirft bei Fehlern, damit
// der Aufrufer entscheiden kann, ob spaeter erneut versucht wird.
export async function mangelAbgleichen(pp, protokollId, stationId, userId) {
  if (!pp?.id || !protokollId) return;
  if (istNichtOk(pp)) {
    const { data: vorhanden, error: e1 } = await supabase.from('maengel').select('id, erledigt')
      .eq('protokoll_id', protokollId).eq('pruefpunkt_id', pp.id);
    if (e1) throw e1;
    const beschreibung = mangelBeschreibung(pp);
    if (vorhanden && vorhanden.length > 0) {
      // Beschreibung nachziehen: Bemerkung und Gruende kommen oft erst nach
      // dem Tippen auf n.i.O. - vorher blieb im Mangel "n.i.O. an Pos. X" stehen.
      const { error: e2 } = await supabase.from('maengel')
        .update({ beschreibung }).eq('protokoll_id', protokollId).eq('pruefpunkt_id', pp.id)
        .eq('erledigt', false);
      if (e2) throw e2;
      return;
    }
    const { error: e3 } = await supabase.from('maengel').insert({
      protokoll_id: protokollId,
      station_id: stationId || null,
      pruefpunkt_id: pp.id,
      schweregrad: 'mittel',
      titel: pp.bezeichnung || ('Mangel ' + (pp.pos_nummer || '')),
      beschreibung,
      erledigt: false,
      created_by: userId || null,
    });
    // 23505 = gibt es schon (paralleles Anlegen von einem zweiten Geraet)
    if (e3 && e3.code !== '23505') throw e3;
  } else {
    const { error } = await supabase.from('maengel').delete()
      .eq('protokoll_id', protokollId).eq('pruefpunkt_id', pp.id).eq('erledigt', false);
    if (error) throw error;
  }
}

// --- Senden ------------------------------------------------------------------
// Nach einem Netzfehler nicht jede weitere Eingabe erst 20 s warten lassen:
// Sie ist ja schon gemerkt und geht mit der naechsten Runde raus.
let zuletztOhneNetz = 0;
const RUHEZEIT_MS = 15000;

// Sendet den gemerkten Stand EINES Pruefpunkts. Immer ueber inReihe aufrufen.
// Ergebnis: { status: 'gesendet' | 'wartet' | 'nichts', data? }
// Wirft nur, wenn die Datenbank die Antwort wirklich ablehnt.
export async function sendePruefpunkt(ppId, { ausWarteschlange = false } = {}) {
  const eintrag = lesen()[ppId];
  if (!eintrag) return { status: 'nichts' };
  if (!navigator.onLine) return { status: 'wartet' };
  if (!ausWarteschlange && Date.now() - zuletztOhneNetz < RUHEZEIT_MS) return { status: 'wartet' };

  const version = eintrag.version || 0;
  const patch = eintrag.patch || ohneId(eintrag.pp);
  let data = null;
  try {
    const res = await withTimeout(
      supabase.from('protokoll_modul_pruefpunkte').update(patch).eq('id', ppId).select().maybeSingle(),
      20000, 'Prüfpunkt speichern'
    );
    if (res.error) throw res.error;
    if (!res.data) {
      // Keine Zeile getroffen: Pruefpunkt geloescht (Planung entfernt) oder
      // keine Berechtigung mehr. Nicht wegwerfen, sondern aufbewahren.
      throw new Error('Prüfpunkt nicht gefunden oder keine Berechtigung (Protokoll gelöscht oder neu zugewiesen?)');
    }
    data = res.data;
  } catch (e) {
    if (nochmalVersuchen(e) || await ohneGueltigeAnmeldung()) {
      zuletztOhneNetz = Date.now();
      return { status: 'wartet' };
    }
    const alle = lesen();
    if (alle[ppId]) { ablegenAlsAbgelehnt(ppId, alle[ppId], e); delete alle[ppId]; schreiben(alle); }
    throw e;
  }

  // Mangel nach dem Stand, der gerade gesendet wurde. Liegt schon ein neuerer
  // Stand vor, macht dessen eigener Durchlauf den Abgleich.
  // Seit v57 nur, wenn sich am Mangel etwas aendern kann: Vorher ging bei JEDER
  // Eingabe (auch Zusatzwerte, Temperatur ...) eine zusaetzliche Anfrage raus -
  // bei schwachem Netz rund 300 unnoetige Anfragen je Protokoll.
  const neuerStand = { ...eintrag.pp, ...data };
  const betrifftStatus = ['in_ordnung', 'wert_auswahl'].some(k => k in patch);
  const betrifftText = ['wert_modifiers', 'bemerkung'].some(k => k in patch);
  const mangelPruefen = betrifftStatus || (betrifftText && istNichtOk(neuerStand));
  if (mangelPruefen) {
    try {
      await withTimeout(
        mangelAbgleichen(neuerStand, eintrag.protokollId, eintrag.stationId, eintrag.userId),
        20000, 'Mangel abgleichen'
      );
    } catch (e) {
      if (nochmalVersuchen(e)) {
        // Antwort ist gespeichert, der Mangel noch nicht: Eintrag bleibt,
        // der naechste Durchlauf holt den Mangel nach (Senden ist harmlos doppelt).
        zuletztOhneNetz = Date.now();
        return { status: 'wartet', data };
      }
      console.error('[Warteschlange] Mangel konnte nicht abgeglichen werden:', ppId, e);
      ablegenAlsAbgelehnt(ppId + '-mangel', eintrag, e);
    }
  }

  vergessenWennUnveraendert(ppId, version);
  return { status: 'gesendet', data };
}

// --- Abschluss (Bewertung, Unterschrift, Status) -----------------------------
// Vom Server abgelehnter Abschluss dieses Protokolls ({ patch, fehler } oder null).
export function abschlussAbgelehnt(protokollId) {
  if (!protokollId) return null;
  const e = lesenVon(ABSCHLUSS_ABGELEHNT_PREFIX + protokollId);
  return e && e.patch ? e : null;
}

export function abschlussHolen(protokollId) {
  if (!protokollId) return {};
  return lesenVon(ABSCHLUSS_PREFIX + protokollId);
}
// durchBuero: Korrektur des Bueros an einem schon abgegebenen oder
// freigegebenen Protokoll (v68). Nur dann darf auch ein freigegebenes
// Protokoll geaendert werden - ein liegengebliebener Monteur-Stand weiter nicht.
export function abschlussVormerken(protokollId, patch, durchBuero) {
  if (!protokollId) return;
  const alt = abschlussHolen(protokollId);
  const neu = { ...alt, ...patch };
  if (durchBuero && Object.keys(patch || {}).length > 0) neu.__durchBuero = true;
  const ok = speichern(ABSCHLUSS_PREFIX + protokollId, neu);
  melden();
  return ok;
}
export function abschlussLoeschen(protokollId) {
  if (!protokollId) return;
  entfernen(ABSCHLUSS_PREFIX + protokollId);
  melden();
}

// Sendet einen gemerkten Abschluss. Schutz: Ein alter, liegengebliebener
// Stand darf eine inzwischen erfolgte Buero-Freigabe nicht zuruecksetzen.
export async function abschlussSenden(protokollId) {
  const gemerkt = abschlussHolen(protokollId);
  const { __durchBuero: durchBuero, ...patch } = gemerkt;
  if (Object.keys(patch).length === 0) {
    if (durchBuero) abschlussLoeschen(protokollId);
    return { status: 'nichts' };
  }
  if (!navigator.onLine) return { status: 'wartet' };
  // Erst die Antworten, dann der Abschluss - sonst sieht das Buero ein
  // abgeschlossenes Protokoll mit Luecken.
  if (patch.status === 'monteur_abgeschlossen' && anzahlFuerProtokoll(protokollId) > 0) {
    return { status: 'wartet' };
  }
  let q = supabase.from('protokolle').update(patch).eq('id', protokollId);
  if (patch.status !== 'buero_freigegeben' && !durchBuero) q = q.neq('status', 'buero_freigegeben');
  try {
    const { data: rows, error } = await withTimeout(q.select('id'), 60000, 'Abschluss senden');
    if (error) throw error;
    // Keine Zeile getroffen (v63): Vorher galt das als Erfolg - Unterschrift und
    // Bewertung verschwanden vom Geraet, obwohl nichts gespeichert war.
    if (!rows || rows.length === 0) {
      const { data: jetzt } = await withTimeout(
        supabase.from('protokolle').select('status').eq('id', protokollId).maybeSingle(), 20000, 'Status prüfen');
      throw new Error(!jetzt
        ? 'Abschluss nicht gespeichert: Das Protokoll gibt es nicht mehr oder du hast keine Berechtigung (neu zugewiesen?).'
        : jetzt.status === 'buero_freigegeben'
          ? 'Abschluss nicht gespeichert: Das Büro hat dieses Protokoll bereits freigegeben.'
          : 'Abschluss nicht gespeichert: keine Berechtigung für dieses Protokoll (neu zugewiesen?).');
    }
  } catch (e) {
    if (nochmalVersuchen(e) || await ohneGueltigeAnmeldung()) return { status: 'wartet' };
    const frueher = lesenVon(ABSCHLUSS_ABGELEHNT_PREFIX + protokollId);
    speichern(ABSCHLUSS_ABGELEHNT_PREFIX + protokollId,
      { patch: { ...(frueher?.patch || {}), ...gemerkt }, fehler: String(e?.message || e), abgelehntAm: Date.now() });
    abschlussErledigt(protokollId, gemerkt);
    console.error('[Warteschlange] Abschluss abgelehnt:', protokollId, e);
    throw e;
  }
  abschlussErledigt(protokollId, gemerkt);
  return { status: 'gesendet' };
}

// Nur die Felder entfernen, die genau so gesendet wurden. Kam waehrend des
// Sendens etwas Neueres dazu, bleibt es stehen und geht mit der naechsten
// Runde raus (vorher wurde es mitgeloescht).
function abschlussErledigt(protokollId, gesendet) {
  const cur = abschlussHolen(protokollId);
  for (const [k, v] of Object.entries(gesendet)) {
    if (k === '__durchBuero') continue;
    if (JSON.stringify(cur[k]) === JSON.stringify(v)) delete cur[k];
  }
  // Das Buero-Kennzeichen gilt fuer alles, was noch wartet - erst mit dem
  // letzten Feld entfernen (vorher ging die zweite Korrektur ohne raus und
  // wurde bei freigegebenen Protokollen abgelehnt).
  if (Object.keys(cur).length === 1 && cur.__durchBuero) delete cur.__durchBuero;
  if (Object.keys(cur).length === 0) {
    abschlussLoeschen(protokollId);
  } else {
    speichern(ABSCHLUSS_PREFIX + protokollId, cur);
    melden();
  }
}

// Abschluss-Felder merken und in fester Reihenfolge senden - derselbe Weg fuer
// Tippen, Speichern-Knopf, Abschliessen, Freigabe und Nachsenden. Vorher liefen
// zwei Wege nebeneinander, und ein alter Stand konnte einen neuen ueberholen.
export function abschlussSpeichern(protokollId, patch, durchBuero) {
  abschlussVormerken(protokollId, patch || {}, durchBuero);
  return inReihe('abschluss|' + protokollId, () => abschlussSenden(protokollId));
}

// --- Zeilen: Stammdaten im Protokoll und Erder-Messwerte ---------------------
// art bestimmt, WIE gespeichert wird. Die db-Helfer bereinigen selbst
// (mitgeladene Verknuepfungen raus, Zahlen aus "0,4" usw.).
const ZEILEN_TEXT = {
  kunde: 'Auftraggeber', station: 'Stationsdaten', konfig: 'Stationskonfiguration',
  trafo: 'Trafo-Stammdaten', msfeld: 'MS-Feld', wandler: 'Wandler', messung: 'Erder-Messwert',
};
// Gesendet werden NUR die geaenderten Felder, und zwar per update auf die
// bestehende Zeile. Vorher ging die ganze, im Offline-Fall veraltete Kopie der
// Zeile raus - eine Eingabe aus Kapitel 01 (Bauform) wurde so von einer
// Eingabe in Kapitel 02 (Hersteller) wieder zurueckgesetzt. Nur wenn es die
// Zeile noch nicht gibt (offline angelegtes MS-Feld, fehlende Konfiguration),
// wird angelegt.
function ohneTechnik(p) {
  const k = { ...p };
  delete k.created_at;
  delete k.updated_at;
  return k;
}

async function aktualisierenOderAnlegen(tabelle, schluesselSpalte, zeile) {
  const wert = zeile[schluesselSpalte];
  const aenderung = { ...zeile };
  delete aenderung[schluesselSpalte];
  if (Object.keys(aenderung).length === 0) return { data: null, error: null };
  const res = await supabase.from(tabelle).update(aenderung).eq(schluesselSpalte, wert).select();
  if (res.error) return { data: null, error: res.error };
  if (res.data && res.data.length > 0) return { data: res.data[0], error: null };
  return supabase.from(tabelle).upsert(zeile, { onConflict: schluesselSpalte }).select().single();
}

const ZEILEN_SENDEN = {
  kunde: (p) => {
    const k = ohneTechnik(p);
    delete k.stationen;
    if ('kunden_nr' in k) k.kunden_nr = k.kunden_nr || null;   // '' kollidiert mit UNIQUE
    return aktualisierenOderAnlegen('kunden', 'id', k);
  },
  station: (p) => {
    const s = ohneTechnik(p);
    for (const k of ['kunden', 'station_konfiguration', 'station_trafos', 'vnb']) delete s[k];
    return aktualisierenOderAnlegen('stationen', 'id', s);
  },
  konfig: (p) => {
    const c = konfigBereinigen(p);
    delete c.id;
    return aktualisierenOderAnlegen('station_konfiguration', 'station_id', c);
  },
  trafo:  (p) => aktualisierenOderAnlegen('station_trafos', 'id', trafoBereinigen(p)),
  msfeld: (p) => aktualisierenOderAnlegen('station_ms_felder', 'id', zeileBereinigen(p)),
  wandler: (p) => aktualisierenOderAnlegen('station_wandler', 'id', zeileBereinigen(p)),
  messung: (p) => supabase.from('protokoll_erderanlage_messungen')
    .upsert(p, { onConflict: 'protokoll_id,anschluss_id' }).select().single(),
};

// Noch nicht gesendete Zeilen einer Art: [payload, ...]. Damit zeigen die
// Formulare nach einem Neuladen den Stand des Geraets.
export function offeneZeilen(art) {
  return Object.values(lesenVon(ZEILEN_SCHLUESSEL)).filter(e => e?.art === art).map(e => e.payload);
}

// Wartende Aenderungen EINER Zeile ({} wenn nichts wartet). Fuer Karten, die
// beim Oeffnen den Stand des Geraets zeigen sollen.
export function offeneZeile(art, schluessel) {
  if (schluessel == null) return {};
  return lesenVon(ZEILEN_SCHLUESSEL)[art + ':' + schluessel]?.payload || {};
}

function zeileVormerken(art, schluessel, payload) {
  const k = art + ':' + schluessel;
  const alle = lesenVon(ZEILEN_SCHLUESSEL);
  const alt = alle[k];
  alle[k] = {
    art,
    payload: { ...(alt?.payload || {}), ...payload },
    zeit: Date.now(),
    version: (alt?.version || 0) + 1,
  };
  speichern(ZEILEN_SCHLUESSEL, alle);
  melden();
  return k;
}

async function zeileSenden(k, { ausWarteschlange = false } = {}) {
  const eintrag = lesenVon(ZEILEN_SCHLUESSEL)[k];
  if (!eintrag) return { status: 'nichts' };
  if (!navigator.onLine) return { status: 'wartet' };
  if (!ausWarteschlange && Date.now() - zuletztOhneNetz < RUHEZEIT_MS) return { status: 'wartet' };
  const senden = ZEILEN_SENDEN[eintrag.art];
  if (!senden) return { status: 'nichts' };
  let data = null;
  try {
    const res = await withTimeout(senden(eintrag.payload), 20000, (ZEILEN_TEXT[eintrag.art] || 'Daten') + ' speichern');
    if (res?.error) throw res.error;
    data = res?.data ?? null;
  } catch (e) {
    if (nochmalVersuchen(e) || await ohneGueltigeAnmeldung()) { zuletztOhneNetz = Date.now(); return { status: 'wartet' }; }
    // Nur den abgelehnten Stand entfernen. Liegt schon ein neuerer vor, bleibt
    // er stehen und wird mit seinem eigenen Durchlauf gesendet (vorher wurde
    // er mitgeloescht, und die Anzeige sagte trotzdem "gespeichert").
    try {
      const abgelehnt = lesenVon(ZEILEN_ABGELEHNT);
      abgelehnt[k] = { ...eintrag, payload: { ...(abgelehnt[k]?.payload || {}), ...(eintrag.payload || {}) },
                       fehler: String(e?.message || e), abgelehntAm: Date.now() };
      speichern(ZEILEN_ABGELEHNT, abgelehnt);
      const alle = lesenVon(ZEILEN_SCHLUESSEL);
      if (alle[k] && (alle[k].version || 0) === (eintrag.version || 0)) {
        delete alle[k];
        speichern(ZEILEN_SCHLUESSEL, alle);
      }
    } catch { /* egal */ }
    melden();
    console.error('[Warteschlange] Zeile abgelehnt:', k, e);
    throw e;
  }
  const alle = lesenVon(ZEILEN_SCHLUESSEL);
  if (alle[k] && (alle[k].version || 0) === (eintrag.version || 0)) {
    delete alle[k];
    speichern(ZEILEN_SCHLUESSEL, alle);
    melden();
  }
  return { status: 'gesendet', data };
}

// Erst merken (sofort, im Geraet), dann senden. Ergebnis wie bei den
// Pruefpunkten: { status: 'gesendet' | 'wartet', data }. Wirft nur, wenn die
// Datenbank wirklich ablehnt.
export function zeileSpeichern(art, schluessel, payload) {
  const k = zeileVormerken(art, schluessel, payload);
  return inReihe('zeile|' + k, () => zeileSenden(k));
}

// --- Abarbeiten --------------------------------------------------------------
let laufend = null;

export function abarbeiten() {
  if (laufend) return laufend;
  if (!navigator.onLine) return Promise.resolve({ gesendet: 0, offen: anzahl() });
  laufend = (async () => {
    let gesendet = 0;
    for (const ppId of Object.keys(lesen())) {
      try {
        const r = await inReihe(ppId, () => sendePruefpunkt(ppId, { ausWarteschlange: true }));
        if (r.status === 'gesendet') gesendet++;
        if (r.status === 'wartet') break;      // weiter kein Netz - spaeter erneut
      } catch {
        // Abgelehnt: ist aufbewahrt und protokolliert, weiter mit dem naechsten.
      }
    }
    for (const k of Object.keys(lesenVon(ZEILEN_SCHLUESSEL))) {
      try {
        const r = await inReihe('zeile|' + k, () => zeileSenden(k, { ausWarteschlange: true }));
        if (r.status === 'gesendet') gesendet++;
        if (r.status === 'wartet') break;
      } catch { /* aufbewahrt */ }
    }
    for (const k of abschlussSchluessel()) {
      const protokollId = k.slice(ABSCHLUSS_PREFIX.length);
      try {
        const r = await inReihe('abschluss|' + protokollId, () => abschlussSenden(protokollId));
        if (r.status === 'gesendet') gesendet++;
        if (r.status === 'wartet') break;      // kein Netz - spaeter erneut
      } catch { /* protokolliert */ }
    }
    return { gesendet, offen: anzahl() };
  })().finally(() => { laufend = null; });
  return laufend;
}

// --- Speichern-Knopf ---------------------------------------------------------
// Anna-Lena nach dem Testlauf: "Daten verloren, weil kein Speicher-Button im
// Wartungsprotokoll war." Der Knopf macht zweierlei:
//   1. Alle Eingabefelder, die noch kurz warten (Bemerkung, Stammdaten,
//      Ohm-Wert im Feld), geben ihren Stand SOFORT ab (Ereignis
//      'etb-jetzt-speichern'; das aktive Feld verliert den Fokus).
//   2. Alles Gemerkte wird gesendet.
// Ergebnis: { offen, abgelehnt, online }.
export async function jetztSpeichern({ maxWartenMs = 25000 } = {}) {
  try { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); } catch { /* egal */ }
  window.dispatchEvent(new CustomEvent('etb-jetzt-speichern'));
  await new Promise(r => setTimeout(r, 350));
  zuletztOhneNetz = 0;
  if (navigator.onLine) {
    const lauf = (async () => { await abarbeiten(); await abarbeiten(); })();
    await Promise.race([lauf, new Promise(r => setTimeout(r, maxWartenMs))]);
  }
  return { offen: anzahl(), abgelehnt: abgelehnteAnzahl(), online: navigator.onLine, voll: speicherVoll };
}

// Automatik: bei wiederkehrendem Netz und regelmäßig im Hintergrund.
let gestartet = false;
export function starteAutomatik() {
  if (gestartet) return;
  gestartet = true;
  window.addEventListener('online', () => { zuletztOhneNetz = 0; abarbeiten(); });
  // App wird verlassen oder in den Hintergrund geschoben (Handy-Taste,
  // Anruf, App-Wechsel): Offene Eingaben SOFORT ins Geraet schreiben. Das
  // Merken ist synchron und schafft es auch, wenn das System die App gleich
  // danach beendet.
  // Das aktive Feld verliert zusaetzlich den Fokus: Felder, die erst beim
  // Verlassen uebernehmen (Ohm-Wert, Datum), geben ihren Wert so auch ab.
  const sichern = () => {
    try { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); } catch { /* egal */ }
    try { window.dispatchEvent(new CustomEvent('etb-jetzt-speichern')); } catch { /* egal */ }
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') sichern();
    else if (anzahl() > 0) abarbeiten();
  });
  window.addEventListener('pagehide', sichern);
  // Alle 45 s ein Versuch. Bewusst selten: Es geht um Nachsenden, nicht um
  // Echtzeit, und jeder Versuch kostet den Monteur Datenvolumen.
  setInterval(() => { if (anzahl() > 0) abarbeiten(); }, 45000);
  // Beim Start einmal aufräumen, was vom letzten Mal liegen blieb.
  if (anzahl() > 0) abarbeiten();
}
